/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * HOGAR SEGURO — Indicador de cupón activo (vía enlace ?discount=CODIGO)
 * ------------------------------------------------------------------------------------------------------------
 * Los descuentos de tipo "Código" que se activan por URL (?discount=CODIGO) quedan asociados a la sesión de
 * checkout, pero Shopify NO los refleja en el objeto `cart` (Liquid) hasta que el cliente llega al checkout.
 * Esto genera el hueco visual: el carrito no muestra que el cupón está "pendiente" de aplicarse.
 *
 * Esta pieza:
 *   1. Captura el parámetro `discount` de la URL al aterrizar en cualquier página y lo guarda en sessionStorage.
 *   2. Define el custom element <hs-coupon-banner>, que se auto-inicializa cada vez que es insertado en el DOM
 *      (incluido cuando Focal re-renderiza el mini-carrito completo tras "cart:refresh" — ver CartDrawer en
 *      theme.js). No requiere suscribirse manualmente a "cart:updated"/"cart:refresh": el propio ciclo de vida
 *      del custom element (connectedCallback) se encarga de refrescar el banner cada vez que el HTML del carrito
 *      se reemplaza.
 *
 * IMPORTANTE — limitación conocida: esto es un indicador de UI basado en el parámetro de la URL, no una
 * validación real del descuento. La validación real solo ocurre en checkout. Si el código requiere condiciones
 * adicionales no verificables en el cliente (ej. "HogarSeguro" solo aplica a 30 productos específicos), el
 * banner solo verifica el monto mínimo del carrito, no la elegibilidad de producto — puede mostrar el banner
 * aunque el descuento finalmente no aplique en checkout por esa condición. Comunicar esto al cliente.
 */
(function () {
  var STORAGE_KEY = 'hs_pending_discount_code';

  // 1. Capturar el código de la URL en cualquier página de entrada (pop-up, WhatsApp, email)
  var params = new URLSearchParams(window.location.search);
  var discountCode = params.get('discount');
  if (discountCode) {
    try {
      sessionStorage.setItem(STORAGE_KEY, discountCode);
    } catch (e) {
      // sessionStorage no disponible (modo privado extremo, etc.) — degradar sin romper el sitio
    }
  }

  // 2. Custom element del banner, usado en mini-cart.liquid y main-cart.liquid
  class HsCouponBanner extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      var code;
      try {
        code = sessionStorage.getItem(STORAGE_KEY);
      } catch (e) {
        code = null;
      }

      if (!code) {
        this.hidden = true;
        return;
      }

      var total = parseInt(this.dataset.cartTotal, 10) || 0;
      var threshold = parseInt(this.dataset.threshold, 10) || 0;
      var textEl = this.querySelector('[data-coupon-text]');
      if (!textEl) return;

      if (total >= threshold) {
        textEl.textContent = 'Cupón ' + code + ' recibido. Su aplicación y descuento se confirman al pagar.';
        this.classList.remove('hs-coupon-banner--pending');
        this.classList.add('hs-coupon-banner--active');
      } else {
        var remaining = (threshold - total) / 100;
        var remainingFormatted = remaining.toLocaleString('es-CL', { maximumFractionDigits: 0 });
        textEl.textContent = 'Te faltan $' + remainingFormatted + ' para alcanzar el mínimo de tu cupón ' + code + '. Sujeto a condiciones; se confirma al pagar.';
        this.classList.remove('hs-coupon-banner--active');
        this.classList.add('hs-coupon-banner--pending');
      }

      this.hidden = false;
    }
  }

  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('hs-coupon-banner')) {
    window.customElements.define('hs-coupon-banner', HsCouponBanner);
  }
})();